import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'

const PLUGIN_KEY = 'bth_influence_attack'
const EMPTY_CONFIG = Object.freeze({})
const cloneValue = value => typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value ?? {}))
const digits = value => String(value ?? '').replace(/[^\d]/g, '')
const bounded = (value, fallback, minimum, maximum) => {
    const parsed = Number.parseInt(String(value ?? ''), 10)
    return String(Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, parsed)) : fallback)
}
const positiveIDs = value => [...new Set(
    (Array.isArray(value) ? value : String(value ?? '').split(/[,;\s]+/))
        .map(entry => Number(entry))
        .filter(entry => Number.isInteger(entry) && entry > 0)
)].sort((left, right) => left - right)

export const normalizeBthInfluenceSettings = pluginConfig => ({
    dryRun: pluginConfig?.dryRun !== false,
    autoCollectTax: pluginConfig?.autoCollectTax === true,
    taxCollectionType: bounded(pluginConfig?.taxCollectionType, 0, 0, 6),
    allowPremiumTaxPeriods: pluginConfig?.allowPremiumTaxPeriods === true,
    taxSafetyDelaySeconds: bounded(pluginConfig?.taxSafetyDelaySeconds, 1, 1, 60),
    taxPollSeconds: bounded(pluginConfig?.taxPollSeconds, 15, 5, 300),
    attackPlayers: pluginConfig?.attackPlayers !== false,
    attackCapitals: pluginConfig?.attackCapitals !== false,
    capitalSelectionMode: pluginConfig?.capitalSelectionMode === 'all' ? 'all' : 'selected',
    selectedCapitalAllianceIDs: positiveIDs(pluginConfig?.selectedCapitalAllianceIDs),
    playerSelectionMode: pluginConfig?.playerSelectionMode === 'all' ? 'all' : 'selected',
    selectedPlayerIDs: positiveIDs(pluginConfig?.selectedPlayerIDs),
    minPlayerInfluence: bounded(pluginConfig?.minPlayerInfluence, 1000, 0, 1000000000),
    minAllianceInfluence: bounded(pluginConfig?.minAllianceInfluence, 1000, 0, 1000000000),
    scanRadius: bounded(pluginConfig?.scanRadius, 100, 10, 500),
    scanIntervalSeconds: bounded(pluginConfig?.scanIntervalSeconds, 120, 30, 86400),
    maxInfluenceChecks: bounded(pluginConfig?.maxInfluenceChecks, 100, 1, 500),
    maxConcurrentAttacks: bounded(pluginConfig?.maxConcurrentAttacks, 1, 1, 20),
    targetCooldownSeconds: bounded(pluginConfig?.targetCooldownSeconds, 1800, 60, 604800),
    attackedTargetIgnoreSeconds: bounded(pluginConfig?.attackedTargetIgnoreSeconds, 10800, 60, 604800),
    priority: ['efficiency', 'expected', 'influence', 'nearest'].includes(pluginConfig?.priority) ? pluginConfig.priority : 'efficiency',
    commanderWhiteList: String(pluginConfig?.commanderWhiteList ?? ''),
    minTroops: bounded(pluginConfig?.minTroops, 100, 1, 5000),
    reserveTroops: bounded(pluginConfig?.reserveTroops, 0, 0, 1000000),
    waves: bounded(pluginConfig?.waves, 4, 1, 4),
    useCoin: pluginConfig?.useFeathers === true ? false : pluginConfig?.useCoin === true,
    useFeathers: pluginConfig?.useFeathers === true,
    attackPresetSlot: String(pluginConfig?.attackPresetSlot ?? ''),
    attackPresetApplyMode: ['firstWave', 'allWaves'].includes(pluginConfig?.attackPresetApplyMode) ? pluginConfig.attackPresetApplyMode : 'firstWave',
    ignoredPlayerIDs: String(pluginConfig?.ignoredPlayerIDs ?? ''),
    ignoredAllianceIDs: String(pluginConfig?.ignoredAllianceIDs ?? ''),
    ignoredNames: String(pluginConfig?.ignoredNames ?? '')
})

const numericFields = [
    ['minPlayerInfluence', 'Minimum player influence', 'Players below this available influence are not attacked.', 0, 1000000000],
    ['minAllianceInfluence', 'Minimum capital influence', 'Capitals below this server-reported pending amount are not attacked.', 0, 1000000000],
    ['scanRadius', 'Map scan radius', 'Distance around the BTH main castle (10-500).', 10, 500],
    ['scanIntervalSeconds', 'Scan interval (seconds)', 'Minimum 30 seconds.', 30, 86400],
    ['maxInfluenceChecks', 'Influence checks per target type', 'Maximum capital checks plus the same maximum number of player checks.', 1, 500],
    ['targetCooldownSeconds', 'Target cooldown (seconds)', 'Prevents repeatedly selecting the same coordinates.', 60, 604800],
    ['attackedTargetIgnoreSeconds', 'Ignore after successful attack (seconds)', 'The attacked player or alliance disappears from both lists. Default: 10,800 seconds (3 hours).', 60, 604800],
    ['maxConcurrentAttacks', 'Maximum outgoing attacks', 'Local limit for BTH attacks before impact.', 1, 20],
    ['minTroops', 'Minimum troops per attack', 'The attack is cancelled if fewer can be assigned.', 1, 5000],
    ['reserveTroops', 'Troops kept in castle', 'These eligible attackers are never assigned.', 0, 1000000],
    ['waves', 'Attack waves', 'Uses between 1 and 4 normal waves.', 1, 4]
]

const phaseLabel = phase => ({
    waiting: 'Waiting',
    waiting_for_bth: 'Waiting for BTH',
    disabled: 'Disabled',
    scanning_map: 'Scanning map',
    checking_influence: 'Checking influence',
    scan_only: 'Scan only',
    simulation: 'Simulation',
    ready: 'Ready',
    sending_attack: 'Sending attack',
    waiting_for_commander: 'Waiting for commander',
    movement_limit: 'Movement limit',
    error: 'Error'
}[phase] ?? 'Waiting')

const formatNumber = value => Number(value || 0).toLocaleString('en-US')

export const buildBthAllianceForecasts = candidates => (Array.isArray(candidates) ? candidates : [])
    .filter(target => target?.type === 'capital' && Number(target?.allianceID) > 0)
    .map(target => {
        const influencePool = Math.max(0, Number(target.pendingInfluence ?? target.influence) || 0)
        const expectedSteal = Math.max(0, Number(target.expectedInfluence) || Math.floor(influencePool * .15))
        const distance = Math.max(0, Number(target.distance) || 0)
        const efficiency = expectedSteal / Math.max(1, distance)
        const risk = !target.eligible || influencePool <= 0 ? 'low' : distance > 150 || efficiency < 20 ? 'high' : efficiency < 75 ? 'medium' : 'low'
        return { ...target, influencePool, expectedSteal, efficiency, risk }
    })
    .sort((left, right) => right.influencePool - left.influencePool || right.expectedSteal - left.expectedSteal || left.distance - right.distance)

export default function BthInfluenceAttackPanel({ userPlugins, onChange, runtimeStatus }) {
    const [forecastExpanded, setForecastExpanded] = React.useState(true)
    const pluginConfig = React.useMemo(() => userPlugins?.[PLUGIN_KEY] ?? EMPTY_CONFIG, [userPlugins])
    const settings = React.useMemo(() => normalizeBthInfluenceSettings(pluginConfig), [pluginConfig])
    const enabled = pluginConfig?.state === true
    const candidates = React.useMemo(
        () => Array.isArray(runtimeStatus?.candidates) ? runtimeStatus.candidates : [],
        [runtimeStatus?.candidates]
    )
    const byInfluenceDescending = (left, right) =>
        Number(right.pendingInfluence ?? right.influence ?? 0) - Number(left.pendingInfluence ?? left.influence ?? 0) ||
        Number(left.distance ?? 0) - Number(right.distance ?? 0)
    const capitalCandidates = candidates.filter(target => target.type === 'capital').sort(byInfluenceDescending)
    const playerCandidates = candidates.filter(target => target.type === 'player').sort(byInfluenceDescending)
    const allianceForecasts = React.useMemo(() => buildBthAllianceForecasts(candidates), [candidates])
    const selectedCapitalIDs = new Set(settings.selectedCapitalAllianceIDs)
    const selectedPlayerIDs = new Set(settings.selectedPlayerIDs)

    const commit = React.useCallback((nextSettings, state = enabled) => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] = {
            ...(nextPlugins[PLUGIN_KEY] ?? {}),
            ...normalizeBthInfluenceSettings(nextSettings),
            state: Boolean(state)
        }
        onChange(nextPlugins)
    }, [enabled, onChange, userPlugins])

    const update = React.useCallback(mutator => {
        const next = cloneValue(settings)
        mutator(next)
        commit(next)
    }, [commit, settings])

    const setCapitalSelected = React.useCallback((allianceID, selected) => {
        update(next => {
            const ids = new Set(positiveIDs(next.selectedCapitalAllianceIDs))
            if (selected)
                ids.add(Number(allianceID))
            else
                ids.delete(Number(allianceID))
            next.selectedCapitalAllianceIDs = [...ids]
        })
    }, [update])

    const setPlayerSelected = React.useCallback((playerID, selected) => {
        update(next => {
            const ids = new Set(positiveIDs(next.selectedPlayerIDs))
            if (selected)
                ids.add(Number(playerID))
            else
                ids.delete(Number(playerID))
            next.selectedPlayerIDs = [...ids]
        })
    }, [update])

    const renderTarget = (target, index) => (
        <Paper key={`${target.type}-${target.x}-${target.y}`} variant="outlined" sx={{ p: 1.5, borderRadius: '14px', borderColor: target.eligible ? 'rgba(102,187,106,.38)' : 'rgba(155,180,216,.18)', bgcolor: 'rgba(12,23,41,.54)' }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Stack direction="row" spacing={.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Checkbox
                        sx={{ mt: -.8, ml: -.8 }}
                        disabled={target.type === 'capital' ? settings.capitalSelectionMode === 'all' : settings.playerSelectionMode === 'all'}
                        checked={target.type === 'capital' ?
                            settings.capitalSelectionMode === 'all' || selectedCapitalIDs.has(Number(target.allianceID)) :
                            settings.playerSelectionMode === 'all' || selectedPlayerIDs.has(Number(target.ownerID))}
                        onChange={event => target.type === 'capital' ?
                            setCapitalSelected(target.allianceID, event.target.checked) :
                            setPlayerSelected(target.ownerID, event.target.checked)}
                        inputProps={{ 'aria-label': target.type === 'capital' ? `Select capital alliance ${target.allianceID}` : `Select player ${target.ownerID}` }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={850}>#{index + 1} {target.type === 'capital' ? 'Capital' : target.ownerName || 'Player'} · {target.x}:{target.y}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {target.allianceName || 'No alliance'}{target.allianceID ? ` (#${target.allianceID})` : ''} · distance {target.distance}
                        </Typography>
                        <Chip size="small" color={target.eligible ? 'success' : 'default'} label={target.eligible ? 'Above attack threshold' : 'Below attack threshold'} sx={{ mt: .7 }} />
                    </Box>
                </Stack>
                <Stack alignItems="flex-end" sx={{ minWidth: 125 }}>
                    <Typography variant="caption" color="text.secondary">Available before collection</Typography>
                    <Typography fontWeight={850}>{formatNumber(target.pendingInfluence ?? target.influence)}</Typography>
                    <Typography variant="caption" color="success.light">≈ {formatNumber(target.expectedInfluence)} expected steal</Typography>
                </Stack>
            </Stack>
        </Paper>
    )

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '22px', bgcolor: 'rgba(18,29,48,.82)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Stack direction="row" spacing={1.2} alignItems="flex-start">
                        <GpsFixedRoundedIcon sx={{ color: '#ffb74d', mt: .35 }} />
                        <Box>
                            <Typography variant="h5" fontWeight={900}>BTH Influence Attacks</Typography>
                            <Typography color="text.secondary">
                                Read influence before capital collection, rank targets, and attack only the capitals you select.
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip color={runtimeStatus?.phase === 'error' ? 'error' : enabled ? 'success' : 'default'} label={phaseLabel(runtimeStatus?.phase)} />
                        <Chip color={settings.dryRun ? 'warning' : 'error'} label={settings.dryRun ? 'Simulation' : 'LIVE attacks'} />
                        <Chip label={`${runtimeStatus?.activeAttacks ?? 0} outgoing`} />
                        <Chip label={`${runtimeStatus?.attacksSent ?? 0} sent`} />
                    </Stack>
                </Stack>
            </Paper>

            <Alert severity={settings.dryRun ? 'info' : 'warning'} icon={<SecurityRoundedIcon />}>
                {settings.dryRun ?
                    'Safe mode is active: the worker scans and ranks targets but never sends abgcam.' :
                    'LIVE mode is active. Saving with the module enabled allows real BTH attacks.'}
            </Alert>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(25,24,18,.78)', border: '1px solid rgba(255,193,7,.28)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box>
                        <Typography variant="h6" fontWeight={900}>BTH Alliance Forecast</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Live alliance influence pools ranked from highest to lowest. Estimated loot uses the BTH capital rate.
                        </Typography>
                    </Box>
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => setForecastExpanded(value => !value)}
                        endIcon={forecastExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                    >
                        {forecastExpanded ? 'Collapse' : `Expand (${allianceForecasts.length})`}
                    </Button>
                </Stack>
                <Collapse in={forecastExpanded}>
                    {allianceForecasts.length > 0 ? (
                        <Grid container spacing={1.4} sx={{ mt: .5 }}>
                            {allianceForecasts.map((forecast, index) => {
                                const selected = settings.capitalSelectionMode === 'all' || selectedCapitalIDs.has(Number(forecast.allianceID))
                                const riskColor = forecast.risk === 'high' ? 'error' : forecast.risk === 'medium' ? 'warning' : 'success'
                                return (
                                    <Grid item xs={12} md={6} xl={4} key={`forecast-${forecast.allianceID}`}>
                                        <Paper variant="outlined" sx={{ p: 1.6, height: '100%', borderRadius: '14px', borderColor: selected ? 'rgba(255,193,7,.42)' : 'rgba(255,255,255,.12)', bgcolor: 'rgba(10,15,20,.48)' }}>
                                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                <Stack direction="row" spacing={.6} sx={{ minWidth: 0 }}>
                                                    <Checkbox
                                                        size="small"
                                                        sx={{ p: .25, alignSelf: 'flex-start' }}
                                                        disabled={settings.capitalSelectionMode === 'all'}
                                                        checked={selected}
                                                        onChange={event => setCapitalSelected(forecast.allianceID, event.target.checked)}
                                                    />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography fontWeight={900} noWrap>#{index + 1} {forecast.allianceName || `Alliance #${forecast.allianceID}`}</Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {forecast.leaderName ? `Leader: ${forecast.leaderName}` : `Capital owner: ${forecast.ownerName || 'unknown'}`} · Capital {forecast.x}:{forecast.y}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Chip size="small" color={riskColor} label={`${forecast.risk} risk`} />
                                            </Stack>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1.2, mt: 1.5 }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">INFLUENCE POOL</Typography>
                                                    <Typography fontWeight={900}>{formatNumber(forecast.influencePool)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">ESTIMATED LOOT</Typography>
                                                    <Typography fontWeight={900} color="success.light">≈ {formatNumber(forecast.expectedSteal)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">AT RESET</Typography>
                                                    <Typography fontWeight={800} color="text.secondary">Waiting for live data</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">ALLIANCE / LEADER MP</Typography>
                                                    {Number(forecast.allianceMightPoints) > 0 || Number(forecast.leaderMightPoints) > 0 ? (
                                                        <Typography fontWeight={800}>{formatNumber(forecast.allianceMightPoints)} / {formatNumber(forecast.leaderMightPoints)}</Typography>
                                                    ) : (
                                                        <Typography fontWeight={800} color="text.secondary">Loading alliance details…</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Stack direction="row" spacing={.8} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                                                <Chip size="small" variant="outlined" label={`Distance ${forecast.distance}`} />
                                                <Chip size="small" variant="outlined" label={`Alliance #${forecast.allianceID}`} />
                                                <Chip size="small" color={forecast.eligible ? 'success' : 'default'} label={forecast.eligible ? 'Eligible' : 'Below limit'} />
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    ) : (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            No alliance capitals have been measured yet. Start the BTH bot or run it in Simulation mode to populate this forecast.
                        </Alert>
                    )}
                </Collapse>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={event => commit(settings, event.target.checked)} color="success" />}
                            label={enabled ? 'Module enabled' : 'Module disabled'}
                        />
                        <Typography variant="body2" color="text.secondary">The worker is loaded only after this configuration is saved.</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={<Switch checked={settings.dryRun} onChange={event => update(next => { next.dryRun = event.target.checked })} color="warning" />}
                            label="Simulation only"
                        />
                        <Typography variant="body2" color="text.secondary">Keep this on for the first scan.</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Target priority"
                            value={settings.priority}
                            onChange={event => update(next => { next.priority = event.target.value })}
                        >
                            <MenuItem value="efficiency">Expected influence / distance</MenuItem>
                            <MenuItem value="expected">Highest expected influence</MenuItem>
                            <MenuItem value="influence">Highest available influence</MenuItem>
                            <MenuItem value="nearest">Nearest target</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '17px', bgcolor: 'rgba(20,31,52,.72)' }}>
                        <FormControlLabel
                            control={<Switch checked={settings.attackPlayers} onChange={event => update(next => { next.attackPlayers = event.target.checked })} />}
                            label="Attack other players"
                        />
                        <Typography variant="body2" color="text.secondary">Reads the amount exposed by the game and estimates a 10% steal.</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '17px', bgcolor: 'rgba(20,31,52,.72)' }}>
                        <FormControlLabel
                            control={<Switch checked={settings.attackCapitals} onChange={event => update(next => { next.attackCapitals = event.target.checked })} />}
                            label="Attack capitals"
                        />
                        <Typography variant="body2" color="text.secondary">Reads the capital's pending influence before collection and estimates a 15% steal. This is a raid, not a conquest.</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Typography variant="h6" fontWeight={900}>Target attack selection</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Capital selections are saved by alliance ID and player selections by player ID. Unselected targets are never attacked in selected mode.
                </Typography>
                <Grid container spacing={1.5}>
                    {[
                        {
                            type: 'capital', label: 'Capitals', modeKey: 'capitalSelectionMode', idsKey: 'selectedCapitalAllianceIDs',
                            candidates: capitalCandidates, id: target => target.allianceID
                        },
                        {
                            type: 'player', label: 'Players', modeKey: 'playerSelectionMode', idsKey: 'selectedPlayerIDs',
                            candidates: playerCandidates, id: target => target.ownerID
                        }
                    ].map(group => (
                        <Grid item xs={12} md={6} key={group.type}>
                            <Paper variant="outlined" sx={{ p: 1.4, borderRadius: '14px', bgcolor: 'rgba(12,23,41,.35)' }}>
                                <TextField
                                    select fullWidth size="small" label={`${group.label} mode`}
                                    value={settings[group.modeKey]}
                                    onChange={event => update(next => { next[group.modeKey] = event.target.value })}
                                >
                                    <MenuItem value="selected">Only selected {group.label.toLowerCase()}</MenuItem>
                                    <MenuItem value="all">All eligible {group.label.toLowerCase()}</MenuItem>
                                </TextField>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                                    <Button
                                        size="small" variant="outlined"
                                        disabled={group.candidates.length === 0 || settings[group.modeKey] === 'all'}
                                        onClick={() => update(next => { next[group.idsKey] = positiveIDs(group.candidates.map(group.id)) })}
                                    >
                                        Select all visible ({group.candidates.length})
                                    </Button>
                                    <Button
                                        size="small" color="inherit"
                                        disabled={settings[group.idsKey].length === 0 || settings[group.modeKey] === 'all'}
                                        onClick={() => update(next => { next[group.idsKey] = [] })}
                                    >
                                        Clear
                                    </Button>
                                    <Chip size="small" label={`${settings[group.idsKey].length} saved`} />
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Typography variant="h6" fontWeight={900} sx={{ mb: 1.7 }}>Scan and attack limits</Typography>
                <Grid container spacing={1.6}>
                    {numericFields.map(([key, label, helperText, minimum, maximum]) => (
                        <Grid item xs={12} sm={6} lg={4} key={key}>
                            <TextField
                                fullWidth
                                label={label}
                                value={settings[key]}
                                onChange={event => update(next => { next[key] = digits(event.target.value) })}
                                onBlur={() => update(next => { next[key] = bounded(next[key], Number(settings[key]), minimum, maximum) })}
                                helperText={helperText}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Typography variant="h6" fontWeight={900} sx={{ mb: 1.7 }}>Commander and formation</Typography>
                <Grid container spacing={1.6}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Normal commander slots"
                            value={settings.commanderWhiteList}
                            onChange={event => update(next => { next.commanderWhiteList = event.target.value })}
                            helperText="Example: 1,3-5. Empty means every available normal commander."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Attack preset"
                            value={settings.attackPresetSlot}
                            onChange={event => update(next => { next.attackPresetSlot = event.target.value })}
                        >
                            <MenuItem value="">Disabled</MenuItem>
                            {Array.from({ length: 12 }, (_, index) => <MenuItem key={index} value={String(index)}>Preset {index + 1}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Preset apply"
                            value={settings.attackPresetApplyMode}
                            onChange={event => update(next => { next.attackPresetApplyMode = event.target.value })}
                        >
                            <MenuItem value="firstWave">First wave</MenuItem>
                            <MenuItem value="allWaves">All waves</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={<Switch checked={settings.useCoin} onChange={event => update(next => { next.useCoin = event.target.checked; if (event.target.checked) next.useFeathers = false })} />}
                            label="Use coin horses"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={<Switch checked={settings.useFeathers} onChange={event => update(next => { next.useFeathers = event.target.checked; if (event.target.checked) next.useCoin = false })} />}
                            label="Use feathers"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No commander ability is required. Only availability, a valid lord ID, troop count, and server movement limits can block the attack.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Typography variant="h6" fontWeight={900} sx={{ mb: 1.7 }}>Safety exclusions</Typography>
                <Grid container spacing={1.6}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Ignored player IDs" value={settings.ignoredPlayerIDs} onChange={event => update(next => { next.ignoredPlayerIDs = event.target.value })} helperText="Comma-separated IDs." />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Ignored alliance IDs" value={settings.ignoredAllianceIDs} onChange={event => update(next => { next.ignoredAllianceIDs = event.target.value })} helperText="Comma-separated IDs." />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Ignored names" value={settings.ignoredNames} onChange={event => update(next => { next.ignoredNames = event.target.value })} helperText="Player or alliance names, comma-separated." />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 2.2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={900}>Live measured targets</Typography>
                        <Typography variant="body2" color="text.secondary">{runtimeStatus?.message || 'Save and start the bot in BTH to receive the first scan.'}</Typography>
                        {runtimeStatus?.lastScanAt ? <Typography variant="caption" color="text.secondary">Last server measurement: {new Date(runtimeStatus.lastScanAt).toLocaleString()}</Typography> : null}
                    </Box>
                    {runtimeStatus?.lastError ? <Chip color="error" label={runtimeStatus.lastError} /> : null}
                </Stack>
                {candidates.length > 0 ? (
                    <Grid container spacing={1.5} alignItems="flex-start">
                        <Grid item xs={12} lg={6}>
                            <Paper variant="outlined" sx={{ p: 1.3, borderRadius: '16px', borderColor: 'rgba(255,183,77,.28)', bgcolor: 'rgba(12,23,41,.3)' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                                    <Typography fontWeight={900}>Capitals</Typography>
                                    <Chip size="small" label={`${capitalCandidates.length} found`} />
                                </Stack>
                                <Stack spacing={1.1}>
                                    {capitalCandidates.length > 0 ? capitalCandidates.map(renderTarget) : <Alert severity="info">No capitals found in this scan.</Alert>}
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <Paper variant="outlined" sx={{ p: 1.3, borderRadius: '16px', borderColor: 'rgba(100,181,246,.28)', bgcolor: 'rgba(12,23,41,.3)' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                                    <Typography fontWeight={900}>Players</Typography>
                                    <Chip size="small" label={`${playerCandidates.length} found`} />
                                </Stack>
                                <Stack spacing={1.1}>
                                    {playerCandidates.length > 0 ? playerCandidates.map(renderTarget) : <Alert severity="info">No players found in this scan.</Alert>}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                ) : <Alert severity="info">No live candidates have been reported yet.</Alert>}
            </Paper>
        </Box>
    )
}
